import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { usePro, PRO_FEATURES, WIDGET_STYLES } from '../contexts/ProContext';
import {
  Clock,
  MapPin,
  Compass,
  Scan,
  XCircle,
  BookOpen,
  Search,
  TrendingUp,
  Bookmark,
  Settings,
  Maximize2,
  Crown,
  Radio,
  Home,
  GraduationCap,
} from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

// Tasbih beads icon (custom SVG)
const TasbihIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="4" r="2" />
    <circle cx="12" cy="10" r="2" />
    <circle cx="12" cy="16" r="2" />
    <circle cx="6" cy="7" r="2" />
    <circle cx="18" cy="7" r="2" />
    <circle cx="6" cy="13" r="2" />
    <circle cx="18" cy="13" r="2" />
    <path d="M12 18 L12 22" />
  </svg>
);

// Get the FAB icon based on selected widget style
// Uses the ROUND image (fabImagePath) for the floating action button
const getFabIcon = (selectedStyleId) => {
  const style = WIDGET_STYLES[selectedStyleId] || WIDGET_STYLES.kidsparadise; // Default to Kids Paradise
  
  // Use the round FAB image if available
  if (style.fabImagePath) {
    return {
      type: 'round',
      src: style.fabImagePath,
      alt: style.name,
    };
  }
  
  // Fallback to square image if no round image
  if (style.useImage && style.imagePath) {
    return {
      type: 'image',
      src: style.imagePath,
      alt: style.name,
    };
  }
  
  // Default fallback to first style's round image
  return {
    type: 'round',
    src: '/images/widgets/round/kids-paradise_round.png',
    alt: 'Kids Paradise',
  };
};

const FloatingActionButton = () => {
  const { t } = useTranslation();
  const { appTheme, mode } = useTheme();
  const { settings } = useSettings();
  const { isPro, requestFeatureAccess, isFeatureUnlockedViaAd } = usePro();
  
  // Get selected widget style from localStorage
  const [selectedWidgetStyle, setSelectedWidgetStyle] = useState('kidsparadise'); // Default to Kids Paradise (first free theme style)
  
  useEffect(() => {
    const savedStyle = localStorage.getItem('quranplus-widget-style');
    if (savedStyle && WIDGET_STYLES[savedStyle]) {
      setSelectedWidgetStyle(savedStyle);
    }
    
    // Listen for storage changes (cross-tab)
    const handleStorageChange = (e) => {
      if (e.key === 'quranplus-widget-style' && e.newValue && WIDGET_STYLES[e.newValue]) {
        setSelectedWidgetStyle(e.newValue);
      }
    };
    
    // Listen for custom event (same-tab)
    const handleWidgetStyleChanged = (e) => {
      if (e.detail?.styleId && WIDGET_STYLES[e.detail.styleId]) {
        setSelectedWidgetStyle(e.detail.styleId);
      }
    };
    
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('widgetStyleChanged', handleWidgetStyleChanged);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('widgetStyleChanged', handleWidgetStyleChanged);
    };
  }, []);
  
  // Check if FAB/widget is unlocked via ad
  const hasFabAccess = isPro || isFeatureUnlockedViaAd('bottom_nav');
  
  // Auto-expand FAB if widget mode is enabled
  const [isExpanded, setIsExpanded] = useState(false);
  const [hasAutoExpanded, setHasAutoExpanded] = useState(false);
  
  // Widget mode: auto-expand FAB on mount or when settings change
  React.useEffect(() => {
    // Only auto-expand once per app session when widget mode is enabled
    if (hasFabAccess && settings.widgetMode && settings.floatingMenuStyle === 'fab' && !hasAutoExpanded) {
      console.log('[FAB] Widget mode enabled - auto-expanding FAB');
      setIsExpanded(true);
      setHasAutoExpanded(true);
    }
  }, [hasFabAccess, settings.widgetMode, settings.floatingMenuStyle, hasAutoExpanded]);
  
  // Auto-collapse FAB after 5 seconds of being expanded
  React.useEffect(() => {
    if (!isExpanded) return;
    
    const collapseTimer = setTimeout(() => {
      setIsExpanded(false);
      console.log('[FAB] Auto-collapsed after 5 seconds');
    }, 5000);
    
    return () => clearTimeout(collapseTimer);
  }, [isExpanded]);
  
  // Don't render FAB for free users (unless ad-unlocked)
  if (!hasFabAccess) {
    return null;
  }
  
  // Don't render if bottom dock style
  if (settings.floatingMenuStyle === 'bottom') {
    return null;
  }
  
  // Don't render if using hamburger style
  if (!settings.floatingMenuStyle || settings.floatingMenuStyle === 'hamburger') {
    return null;
  }
  
  // FAB items - Tajweed Coach positioned BELOW Masjid Guide (when viewed in natural order)
  // Note: FAB displays bottom-to-top, so items appear in reverse visual order
  // STANDARDIZATION MANDATE: Tajweed Coach must be after Masjid Guide/Connect in all menus
  const fabItems = [
    // Top of list (seen after scrolling) - Settings first when reversed
    { id: 'settings', icon: Settings, label: t('nav.settings', 'Settings'), href: '/settings' },
    { id: 'search', icon: Search, label: t('nav.search', 'Search'), href: '/search' },
    { id: 'bookmarks', icon: Bookmark, label: t('nav.saved', 'Saved'), href: '/bookmarks' },
    { id: 'qibla', icon: Compass, label: t('nav.qibla', 'Qibla'), href: '/qibla' },
    { id: 'zoom', icon: Maximize2, label: t('nav.zoom', 'Zoom'), href: '/quran/zoom/1', isPro: true, featureId: 'zoom_mode' },
    { id: 'progress', icon: TrendingUp, label: t('nav.progress', 'Progress'), href: '/progress', isPro: true, featureId: 'reading_stats' },
    { id: 'halal', icon: Scan, label: t('nav.halalScanner', 'Halal Scanner'), href: '/halal-scanner', highlight: true, isPro: true, featureId: 'halal_scanner' },
    // TAJWEED COACH - NOW AFTER HALAL SCANNER (visually below Masjid items when FAB opens)
    { id: 'tajweed-coach', icon: GraduationCap, label: t('nav.tajweedCoach', 'Tajweed Coach'), href: '/quran?tajweed=true', isPro: true, featureId: 'tajweed_coach' },
    { id: 'masjid-connect', icon: Radio, label: t('nav.masjidConnect', 'Masjid Connect'), href: '/prayer-times#masjid-connect', isPro: true, featureId: 'masjid_connect' },
    { id: 'mosque', icon: MapPin, label: t('nav.masjidGuide', 'Masjid Guide'), href: '/mosques', isPro: true, featureId: 'mosque_finder' },
    { id: 'hadith', icon: BookOpen, label: t('nav.hadith', 'Hadith'), href: '/hadith' },
    { id: 'tasbih', icon: TasbihIcon, label: t('nav.duas', 'Duas'), href: '/duas' },
    { id: 'prayer', icon: Clock, label: t('nav.prayerTimes', 'Prayer Times'), href: '/prayer-times' },
    { id: 'quran', icon: BookOpen, label: t('nav.quran', 'Quran'), href: '/quran' },
    // Bottom of list (seen first when opening FAB) - Home last when reversed
    { id: 'home', icon: Home, label: t('nav.home', 'Home'), href: '/' },
  ];
  
  // Get theme-specific styles for FAB
  const getFabStyle = () => {
    if (appTheme === 'emerald') {
      return {
        // Dark green background for FAB button
        main: 'bg-[#0d3320] text-[#c9a227] shadow-lg shadow-[#0d3320]/50',
        close: 'bg-red-500 text-white',
        item: 'bg-[#1a2f38] text-white hover:bg-[#243d47]',
        label: 'bg-[#1a2f38]/90 text-white',
      };
    } else if (appTheme === 'royal') {
      return {
        // Keep same background but T icon is purple (handled separately)
        main: 'bg-gradient-to-br from-[#6b21a8] to-[#581c87] text-white shadow-lg shadow-purple-500/30',
        close: 'bg-red-500 text-white',
        item: 'bg-[#16213e] text-[#f5e6d3] border border-[#c9a227]/30 hover:bg-[#1a1a2e]',
        label: 'bg-[#16213e]/90 text-[#f5e6d3] border border-[#c9a227]/20',
      };
    } else if (appTheme === 'kids') {
      return {
        // Keep the same for Kids
        main: 'bg-gradient-to-br from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/30',
        close: 'bg-red-500 text-white',
        item: 'bg-gradient-to-br from-blue-400 to-purple-400 text-white hover:from-blue-500 hover:to-purple-500',
        label: 'bg-purple-500/90 text-white',
      };
    }
    // Classic/Default - Navy blue background
    return {
      main: 'bg-[#1e3a5f] text-white shadow-lg shadow-[#1e3a5f]/50',
      close: 'bg-red-500 text-white',
      item: 'bg-[#1e3a5f] text-white border border-white/20 shadow-md hover:bg-[#2a4a73]',
      label: 'bg-[#1e3a5f]/90 text-white border border-white/20',
    };
  };
  
  const fabStyle = getFabStyle();
  
  return (
    <>
      {/* FAB Container - positioned at lower LEFT corner */}
      <div 
        className="fixed bottom-6 left-4 z-40 lg:bottom-6"
        data-testid="fab-container"
      >
        {/* Expanded Menu - SCROLLABLE vertically, shows limited items */}
        {isExpanded && (
          <div 
            className="absolute bottom-16 left-0 mb-2 max-h-[320px] overflow-y-auto scrollbar-hide rounded-2xl overscroll-contain touch-pan-y"
            style={{ 
              scrollbarWidth: 'none', 
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
              scrollBehavior: 'smooth'
            }}
          >
            <div className="flex flex-col-reverse items-start gap-3 py-2">
              {fabItems.map((item, index) => {
                const Icon = item.icon;
                const isItemLocked = item.isPro && !isPro && !(item.featureId && isFeatureUnlockedViaAd(item.featureId));
                
                const handleFabItemClick = (e) => {
                  if (isItemLocked) {
                    e.preventDefault();
                    setIsExpanded(false);
                    requestFeatureAccess(
                      { id: item.featureId, name: item.label },
                      () => { window.location.href = item.href; }
                    );
                    return;
                  }
                  setIsExpanded(false);
                };
                
                return (
                  <div
                    key={item.id}
                    className="flex items-center gap-3 animate-in slide-in-from-bottom-2 fade-in"
                    style={{ animationDelay: `${index * 30}ms` }}
                  >
                    {/* Icon first (on the left) */}
                    {/* Use React Router Link for client-side nav to preserve app state
                        (Pro entitlement, settings, contexts). Raw <a href> caused a full
                        page reload on every FAB navigation, which re-ran Pro verification
                        and could flip isPro to false on any transient network hiccup —
                        making the FAB disappear on the next route. */}
                    <Link
                      to={isItemLocked ? '#' : item.href}
                      data-fab-item="true"
                      className={`p-3.5 rounded-full shadow-lg transition-all hover:scale-110 flex-shrink-0 relative ${fabStyle.item}`}
                      onClick={handleFabItemClick}
                    >
                      <Icon className="h-5 w-5" />
                      {isItemLocked && (
                        <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                          <Crown className="h-2.5 w-2.5 text-white" />
                        </span>
                      )}
                    </Link>
                    
                    {/* Label second (on the right) */}
                    <span className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap shadow-lg flex items-center gap-2 ${fabStyle.label}`}>
                      {item.label}
                      {isItemLocked && (
                        <span className="px-1 py-0.5 rounded text-[8px] font-bold bg-gradient-to-r from-amber-500 to-orange-500 text-white flex items-center gap-0.5">
                          <Crown className="h-2 w-2" />
                          PRO
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
            {/* Scroll indicator */}
            <div className="sticky bottom-0 left-0 right-0 h-6 bg-gradient-to-t from-black/30 to-transparent pointer-events-none" />
          </div>
        )}
        
        {/* Main FAB Button - No background, just the widget image */}
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className={`transition-all duration-300 ${
            isExpanded
              ? 'p-2 rounded-full shadow-xl ' + fabStyle.close
              : 'hover:scale-110 drop-shadow-2xl'
          }`}
          data-testid="fab-main-btn"
          aria-label={isExpanded ? 'Close menu' : 'Open Tanzeen+ menu'}
          style={!isExpanded ? { background: 'transparent' } : {}}
        >
          {isExpanded ? (
            <XCircle className="h-7 w-7" />
          ) : (
            <FabIconDisplay selectedStyle={selectedWidgetStyle} />
          )}
        </button>
      </div>
    </>
  );
};

// Component to display FAB icon based on selected widget style
// Uses ROUND images for the FAB (fully circular)
const FabIconDisplay = ({ selectedStyle }) => {
  const fabIcon = getFabIcon(selectedStyle);
  
  // Round image style (new - fully circular)
  if (fabIcon.type === 'round') {
    return (
      <img 
        src={fabIcon.src} 
        alt={fabIcon.alt} 
        className="h-14 w-14 rounded-full object-cover"
        style={{
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
        }}
      />
    );
  }
  
  // Fallback to square image (22% rounded) if round not available
  if (fabIcon.type === 'image') {
    return (
      <img 
        src={fabIcon.src} 
        alt={fabIcon.alt} 
        className="h-12 w-12 object-cover rounded-[22%]"
        style={{
          filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))',
        }}
      />
    );
  }
  
  // Default fallback
  return (
    <img 
      src="/images/widgets/round/kids-paradise_round.png" 
      alt="Kids Paradise" 
      className="h-14 w-14 rounded-full object-cover"
      style={{ filter: 'drop-shadow(0 4px 12px rgba(0,0,0,0.4))' }}
    />
  );
};

export default FloatingActionButton;
